import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { appReducer, type AppState } from './reducer.ts'
import type { AppAction } from './actions.ts'
import { fetchAllData } from '@/lib/data.ts'
import { syncToBackend } from '@/lib/sync.ts'
import { buildMockState } from '@/lib/mock-data.ts'
import { useOrganization } from './OrganizationContext.tsx'

export interface AppContextValue {
  state: AppState
  dispatch: (action: AppAction) => void
  isLoading: boolean
  error: string | null
}

export const AppContext = createContext<AppContextValue | null>(null)

const emptyState: AppState = {
  camps: [],
  partners: [],
  campData: {},
}

export function useAppStore(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) {
    throw new Error('useAppStore must be used within an AppProvider')
  }
  return ctx
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { currentOrganizationId, isLoading: orgsLoading } = useOrganization()
  const [state, localDispatch] = useReducer(appReducer, emptyState)
  const [isLoading, setIsLoading] = useState(true)
  const [error, _setError] = useState<string | null>(null)

  // Re-fetch whenever the active tenant changes
  useEffect(() => {
    if (orgsLoading) return

    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- transitions to loading state for an async fetch
    setIsLoading(true)
    // currentOrganizationId may legitimately be null on a pre-migration DB
    // (no user_organizations table) — fetchAllData falls back to RLS-scoped
    // reads in that case so the dashboard still renders.
    fetchAllData(currentOrganizationId)
      .then((data) => {
        if (cancelled) return
        localDispatch({ type: 'HYDRATE', payload: data })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('Failed to load data from the API:', err)
        if (import.meta.env.VITE_APP_ENV === 'production') {
          localDispatch({ type: 'HYDRATE', payload: emptyState })
        } else {
          localDispatch({ type: 'HYDRATE', payload: buildMockState() })
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [currentOrganizationId, orgsLoading])

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  })

  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  })

  const syncQueueRef = useRef<AppAction[]>([])
  const isSyncingRef = useRef(false)

  const syncDispatch = useCallback(
    (action: AppAction) => {
      if ((action as { type: string }).type === 'HYDRATE') {
        localDispatch(action)
        return
      }

      localDispatch(action)
      syncQueueRef.current.push(action)

      if (!isSyncingRef.current) {
        isSyncingRef.current = true
        setTimeout(async () => {
          while (syncQueueRef.current.length > 0) {
            const nextAction = syncQueueRef.current.shift()!
            try {
              await syncToBackend(nextAction, stateRef.current)
            } catch (err) {
              console.error('API sync failed for action:', nextAction.type, err)
              _setError(tRef.current('misc.appContext.syncFailed', { message: (err as Error).message?.slice(0, 100) }))
              setTimeout(() => _setError(null), 5000)
            }
          }
          isSyncingRef.current = false
        }, 0)
      }
    },
    [],
  )

  return (
    <AppContext.Provider value={{ state, dispatch: syncDispatch, isLoading, error }}>
      {error && !isLoading && (
        <div className="fixed top-4 right-4 z-50 max-w-sm rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400 shadow-lg backdrop-blur">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="flex h-screen w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      ) : state.camps.length === 0 && error ? (
        <div className="flex h-screen w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-destructive">Failed to load data</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AppContext.Provider>
  )
}
